import { monitoring } from "@/services/monitoring";

/**
 * Handling for Next.js Server Action "version skew".
 *
 * When a new build of the app is deployed, the server action IDs referenced by
 * an already-loaded browser tab no longer match the ones the new server knows
 * about. The next action that tab invokes rejects with a
 * "Failed to find Server Action" error. This is expected for actions whose code
 * changed between deployments and cannot be avoided on the server side, so the
 * correct recovery is to load the fresh bundle with a hard reload.
 *
 * (Setting a stable NEXT_SERVER_ACTIONS_ENCRYPTION_KEY removes the much larger
 * class of skew errors caused by the encryption key rotating on every build and
 * across replicas — see README. This module only handles the residual case of
 * an action whose ID genuinely changed.)
 */

// Matched case-insensitively against the error message. Kept deliberately
// narrow so we never hard-reload the page for an unrelated runtime error.
const SKEW_MESSAGE_PATTERN = /failed to find server action/i;

// Reload-loop guard. After a hard reload the tab receives the new bundle whose
// action IDs match the server, so a single reload resolves the skew. If we somehow
// detect skew again within this window we stop reloading and let normal error
// handling take over, rather than spin in a reload loop.
const RELOAD_GUARD_KEY = "__atlivo_sa_skew_reloaded_at";
const RELOAD_GUARD_WINDOW_MS = 15_000;

// In-tab dedupe: many in-flight actions can reject at once right after a deploy.
// We only want to act on the first one.
let recoveryInProgress = false;

function getMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object" && "message" in error) {
    return String((error as { message?: unknown }).message ?? "");
  }
  return "";
}

/**
 * True when the given error is a Next.js server action version-skew error.
 */
export function isServerActionVersionSkew(error: unknown): boolean {
  return SKEW_MESSAGE_PATTERN.test(getMessage(error));
}

function reloadedRecently(): boolean {
  try {
    const last = window.sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (!last) return false;
    return Date.now() - Number(last) < RELOAD_GUARD_WINDOW_MS;
  } catch {
    // sessionStorage unavailable (private mode / SSR) — fail open to in-tab dedupe.
    return false;
  }
}

/**
 * Recover from a server action version-skew error by reloading the page to fetch
 * the latest bundle. Reports the event to monitoring as an informational message
 * (not an exception) so it does not pollute the error feed.
 *
 * @returns `true` if recovery (a reload) was initiated, `false` if the caller
 *          should fall back to its normal error handling.
 */
export function recoverFromServerActionVersionSkew(
  context?: Record<string, unknown>
): boolean {
  if (typeof window === "undefined") return false;
  if (recoveryInProgress) return true;

  if (reloadedRecently()) {
    // We already tried a reload and still hit skew — don't loop.
    return false;
  }

  recoveryInProgress = true;

  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // ignore — in-tab dedupe still prevents repeated reloads this tick.
  }

  monitoring.captureMessage("Server action version skew — reloading to latest build", {
    ...context,
    reason: "next-server-action-version-skew",
  });

  window.location.reload();
  return true;
}

/**
 * Convenience: if `error` is a version-skew error, recover from it.
 * @returns `true` if it was a skew error and recovery was initiated.
 */
export function handleIfServerActionVersionSkew(
  error: unknown,
  context?: Record<string, unknown>
): boolean {
  if (!isServerActionVersionSkew(error)) return false;
  return recoverFromServerActionVersionSkew(context);
}
